// Black-Scholes (European) option pricing + Greeks. Pure functions — this is
// the engine the Option Analytics simulator runs on every slider tick, with the
// live anchor (underlying price, per-leg IV) seeded once from Schwab.
//
// Conventions:
//   S      underlying price
//   K      strike
//   T      time to expiry in YEARS
//   sigma  volatility as a decimal (0.25 = 25%)
//   r      risk-free rate, decimal (0.043 = 4.3%)
//   q      continuous dividend yield, decimal (default 0)
//   type   "CALL" | "PUT"
//
// Greeks are returned in trader-friendly units:
//   delta  per $1 underlying
//   gamma  per $1 underlying
//   theta  per CALENDAR DAY (negative = decay)
//   vega   per 1 vol POINT (1%)
//   rho    per 1% rate

const SQRT2PI = Math.sqrt(2 * Math.PI);

// Standard normal PDF
export function normPdf(x) {
  return Math.exp(-0.5 * x * x) / SQRT2PI;
}

// Error function (Abramowitz & Stegun 7.1.26) — max error ~1.5e-7
function erf(x) {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const t = 1 / (1 + 0.3275911 * ax);
  const y =
    1 -
    ((((1.061405429 * t - 1.453152027) * t + 1.421413741) * t - 0.284496736) * t +
      0.254829592) *
      t *
      Math.exp(-ax * ax);
  return sign * y;
}

// Standard normal CDF
export function normCdf(x) {
  return 0.5 * (1 + erf(x / Math.SQRT2));
}

export function yearsToExpiry(expiry, asOf = new Date()) {
  const exp = expiry instanceof Date ? expiry : new Date(expiry + "T16:00:00"); // ~market close
  const now = asOf instanceof Date ? asOf : new Date(asOf);
  const ms = exp.getTime() - now.getTime();
  return Math.max(0, ms / (365 * 24 * 60 * 60 * 1000));
}

export function intrinsicValue(type, S, K) {
  return type === "PUT" ? Math.max(0, K - S) : Math.max(0, S - K);
}

// Price + Greeks for a single option. Degenerates gracefully at expiry (T<=0)
// or zero vol (returns intrinsic value, terminal Greeks).
export function priceOption(type, S, K, T, sigma, r = 0.043, q = 0) {
  const isPut = type === "PUT";

  if (T <= 0 || sigma <= 0) {
    const intrinsic = intrinsicValue(type, S, K);
    let delta = 0;
    if (S > K) delta = isPut ? 0 : 1;
    else if (S < K) delta = isPut ? -1 : 0;
    else delta = isPut ? -0.5 : 0.5;
    return { price: intrinsic, intrinsic, extrinsic: 0, delta, gamma: 0, theta: 0, vega: 0, rho: 0 };
  }

  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r - q + (sigma * sigma) / 2) * T) / (sigma * sqrtT);
  const d2 = d1 - sigma * sqrtT;
  const Nd1 = normCdf(d1);
  const Nd2 = normCdf(d2);
  const pdfd1 = normPdf(d1);
  const eqT = Math.exp(-q * T);
  const erT = Math.exp(-r * T);

  let price, delta, rho, theta;
  if (isPut) {
    price = K * erT * normCdf(-d2) - S * eqT * normCdf(-d1);
    delta = eqT * (Nd1 - 1);
    rho = (-K * T * erT * normCdf(-d2)) / 100;
    theta =
      (-(S * eqT * pdfd1 * sigma) / (2 * sqrtT) +
        r * K * erT * normCdf(-d2) -
        q * S * eqT * normCdf(-d1)) /
      365;
  } else {
    price = S * eqT * Nd1 - K * erT * Nd2;
    delta = eqT * Nd1;
    rho = (K * T * erT * Nd2) / 100;
    theta =
      (-(S * eqT * pdfd1 * sigma) / (2 * sqrtT) -
        r * K * erT * Nd2 +
        q * S * eqT * Nd1) /
      365;
  }

  const gamma = (eqT * pdfd1) / (S * sigma * sqrtT);
  const vega = (S * eqT * pdfd1 * sqrtT) / 100;
  const intrinsic = intrinsicValue(type, S, K);

  return {
    price,
    intrinsic,
    extrinsic: price - intrinsic,
    delta,
    gamma,
    theta,
    vega,
    rho,
  };
}

// Position math for a multi-leg strategy. Each leg:
//   { type:"CALL"|"PUT", side:"BUY"|"SELL", quantity, strike, expiry, entryPremium, iv }
// Returns net P&L and net Greeks (×100 multiplier, signed by side) at the given
// market state. `ivShiftPts` shifts every leg's IV by N vol points (preserves
// term structure for calendars/diagonals).
export function evaluatePosition(legs, { S, asOf, ivShiftPts = 0, r = 0.043, q = 0 }) {
  const mult = 100;
  let pnl = 0;
  const net = { delta: 0, gamma: 0, theta: 0, vega: 0, rho: 0, value: 0 };
  const perLeg = [];

  for (const leg of legs) {
    const sign = leg.side === "SELL" ? -1 : 1;
    const qty = Number(leg.quantity) || 0;
    const T = yearsToExpiry(leg.expiry, asOf);
    const sigma = Math.max(0, (Number(leg.iv) + ivShiftPts) / 100);
    const g = priceOption(leg.type, S, Number(leg.strike), T, sigma, r, q);

    const entry = Number(leg.entryPremium) || 0;
    const legPnl = sign * qty * mult * (g.price - entry);
    pnl += legPnl;

    net.value += sign * qty * mult * g.price;
    net.delta += sign * qty * mult * g.delta;
    net.gamma += sign * qty * mult * g.gamma;
    net.theta += sign * qty * mult * g.theta;
    net.vega += sign * qty * mult * g.vega;
    net.rho += sign * qty * mult * g.rho;

    perLeg.push({ ...leg, T, sigma, ...g, pnl: legPnl });
  }

  return { pnl, net, perLeg };
}

// Payoff curve: net P&L across a range of underlying prices at a given date.
export function payoffCurve(legs, { asOf, ivShiftPts = 0, r = 0.043, q = 0, points = 60, low, high }) {
  const curve = [];
  for (let i = 0; i <= points; i++) {
    const S = low + ((high - low) * i) / points;
    const { pnl } = evaluatePosition(legs, { S, asOf, ivShiftPts, r, q });
    curve.push({ S, pnl });
  }
  return curve;
}
